import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui-kit'
import { useLingui } from '@lingui/react/macro'
import { useWavesurfer } from '@wavesurfer/react'
import { clsx } from 'clsx'
import { Pause, Play, Repeat, RotateCcw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]

const getSkipSeconds = (duration: number) => {
  if (duration > 0 && duration < 10) return 1
  if (duration >= 10 && duration < 30) return 5
  return 10
}

const formatTime = (input: number) => {
  const sec = Number.isFinite(input) && input > 0 ? input : 0
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

type NectarAudioPlayerAdvancedProps = {
  audioUrl: string
  onProgress?: (progress: number) => void
  onFinish?: () => void
  onError?: () => void
  onPlay?: () => void
  onPause?: () => void
  disabled?: boolean
}

export const NectarAudioPlayerAdvanced = ({
  audioUrl,
  onProgress,
  onFinish,
  onError,
  onPlay,
  onPause,
  disabled
}: NectarAudioPlayerAdvancedProps) => {
  const { t } = useLingui()
  const playerRef = useRef<HTMLDivElement>(null)
  const [rate, setRate] = useState(1)
  const [duration, setDuration] = useState(0)
  const [loop, setLoop] = useState(false)
  const loopRef = useRef(loop)
  loopRef.current = loop
  const callbacksRef = useRef({ onProgress, onFinish, onError, onPlay, onPause })
  callbacksRef.current = { onProgress, onFinish, onError, onPlay, onPause }

  const { wavesurfer, isPlaying, isReady, currentTime } = useWavesurfer({
    container: playerRef,
    url: audioUrl,
    cursorColor: '#39c900',
    cursorWidth: 2,
    dragToSeek: true,
    height: 48,
    waveColor: '#727272',
    progressColor: '#39c900',
    autoplay: false,
    barWidth: 3,
    barGap: 2,
    barRadius: 3,
    barHeight: 6
  })

  useEffect(() => {
    if (!wavesurfer) return

    const handleAudioProcess = () => {
      const progressCallback = callbacksRef.current.onProgress
      if (!progressCallback) return
      const dur = wavesurfer.getDuration()
      if (dur > 0) progressCallback(wavesurfer.getCurrentTime() / dur)
    }
    const handleReady = (dur: number) => setDuration(dur)
    const handleFinish = () => {
      callbacksRef.current.onProgress?.(1)
      callbacksRef.current.onFinish?.()
      if (loopRef.current) void wavesurfer.play(0)
    }
    const handleError = () => callbacksRef.current.onError?.()
    const handlePlay = () => callbacksRef.current.onPlay?.()
    const handlePause = () => callbacksRef.current.onPause?.()

    wavesurfer.on('audioprocess', handleAudioProcess)
    wavesurfer.on('ready', handleReady)
    wavesurfer.on('decode', handleReady)
    wavesurfer.on('finish', handleFinish)
    wavesurfer.on('error', handleError)
    wavesurfer.on('play', handlePlay)
    wavesurfer.on('pause', handlePause)

    return () => {
      wavesurfer.un('audioprocess', handleAudioProcess)
      wavesurfer.un('ready', handleReady)
      wavesurfer.un('decode', handleReady)
      wavesurfer.un('finish', handleFinish)
      wavesurfer.un('error', handleError)
      wavesurfer.un('play', handlePlay)
      wavesurfer.un('pause', handlePause)
    }
  }, [wavesurfer])

  // Re-apply playback rate whenever it changes or the audio (re)loads.
  useEffect(() => {
    if (!wavesurfer || !isReady) return
    const media = wavesurfer.getMediaElement?.()
    if (media) media.playbackRate = rate
  }, [wavesurfer, isReady, rate])

  const togglePlay = () => {
    if (!wavesurfer) return
    const media = wavesurfer.getMediaElement?.()
    if (media?.ended) {
      void wavesurfer.play(0)
      return
    }
    void wavesurfer.playPause()
  }

  const skipSeconds = getSkipSeconds(duration)
  const skip = (seconds: number) => wavesurfer?.skip(seconds)

  const restart = () => {
    if (!wavesurfer) return
    wavesurfer.setTime(0)
    callbacksRef.current.onProgress?.(0)
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="h-12 w-full" ref={playerRef} />

      <div className="border-green max-mobile:flex-col flex items-center gap-1 rounded-xl border border-solid p-2">
        <div className="max-mobile:justify-between max-mobile:w-full flex items-center gap-1">
          <Button
            onClick={restart}
            variant="tertiary"
            size="icon"
            isDisabled={disabled}
            title={t`Restart from beginning`}
            className="hover:text-green! shrink-0 border-0! hover:bg-transparent!"
          >
            <RotateCcw className="h-4 w-4 shrink-0" />
          </Button>

          <Button
            onClick={() => skip(-skipSeconds)}
            variant="tertiary"
            size="small"
            isDisabled={disabled}
            title={t`Rewind ${skipSeconds}s`}
            className="hover:text-green! flex shrink-0 items-center gap-1 border-0! px-2! hover:bg-transparent!"
          >
            -{skipSeconds}s
          </Button>

          <Button
            onClick={togglePlay}
            variant="green"
            size="small"
            isDisabled={disabled}
            className="mx-2 flex w-[100px] shrink-0 items-center gap-1"
          >
            {isPlaying ? (
              <>
                <Pause className="h-4 w-4 shrink-0" /> {t`Pause`}
              </>
            ) : (
              <>
                <Play className="h-4 w-4 shrink-0" /> {t`Play`}
              </>
            )}
          </Button>

          <Button
            onClick={() => skip(skipSeconds)}
            variant="tertiary"
            size="small"
            isDisabled={disabled}
            title={t`Forward ${skipSeconds}s`}
            className="hover:text-green! mr-2! flex shrink-0 items-center gap-1 border-0! px-2! hover:bg-transparent!"
          >
            +{skipSeconds}s
          </Button>
        </div>
        <div className="max-mobile:justify-between max-mobile:w-full flex items-center gap-1">
          <div className="max-mobile: flex items-center gap-1 border-x border-l-0 border-solid border-gray-100 pl-2">
            <Button
              onClick={() => setLoop((l) => !l)}
              variant="tertiary"
              size="icon"
              isDisabled={disabled}
              title={loop ? t`Looping on` : t`Loop`}
              className={clsx('shrink-0 border-0! hover:bg-transparent!', loop ? 'text-green!' : 'hover:text-green!')}
            >
              <Repeat className="h-4 w-4 shrink-0" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={disabled}
                title={t`Playback speed`}
                className="hover:text-green! h-10 w-auto! shrink-0 gap-1 rounded-lg border-0! bg-transparent! px-2 text-[16px]/[24px] font-semibold tabular-nums"
              >
                {rate}x
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="min-w-[5rem] p-1">
                {PLAYBACK_RATES.map((r) => (
                  <DropdownMenuItem
                    key={r}
                    onSelect={() => setRate(r)}
                    className={clsx('justify-center tabular-nums', r === rate && 'text-green bg-gray-100/60')}
                  >
                    {r}x
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="text-body-2 mr-2 ml-auto shrink-0 pl-3 tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
      </div>
    </div>
  )
}
